import { createContext, PropsWithChildren, useContext, useEffect, useMemo } from 'react';

import { useMeetPermissions } from './useMeetPermissions';

interface MeetPermissionsProviderProps {
  roomName: string;
  audioDevice?: MediaDeviceInfo;
  videoDevice?: MediaDeviceInfo;
  toggleAudio?: () => boolean | void;
  toggleVideo?: () => boolean | void;
  value?: ReturnType<typeof useMeetPermissions>;
}

export interface MeetPermissionsBridgeApi {
  refreshPermissions: () => Promise<void>;
  reRequestPermissions: () => Promise<void>;
  setShowPermissionsModal: (value: boolean) => void;
  handleMutedByHost: () => void;
  handleVideoStoppedByHost: () => void;
  isAudioMuted: boolean;
  isVideoMuted: boolean;
}

interface MeetPermissionsContextValue extends ReturnType<typeof useMeetPermissions> {}

const MeetPermissionsContext = createContext<MeetPermissionsContextValue | null>(null);

export function MeetPermissionsProvider({
  children,
  roomName,
  audioDevice,
  videoDevice,
  toggleAudio,
  toggleVideo,
  value: externalValue,
  onApi,
}: PropsWithChildren<MeetPermissionsProviderProps & { onApi?: (api: MeetPermissionsBridgeApi) => void }>) {
  const internalPermissions = useMeetPermissions({
    roomName,
    audioDevice,
    videoDevice,
    toggleAudio,
    toggleVideo,
  });
  const permissions = externalValue ?? internalPermissions;

  const value = useMemo(() => permissions, [permissions]);

  useEffect(() => {
    onApi?.({
      refreshPermissions: permissions.refreshPermissions,
      reRequestPermissions: permissions.reRequestPermissions,
      setShowPermissionsModal: permissions.setShowPermissionsModal,
      handleMutedByHost: permissions.handleMutedByHost,
      handleVideoStoppedByHost: permissions.handleVideoStoppedByHost,
      isAudioMuted: permissions.isAudioMuted,
      isVideoMuted: permissions.isVideoMuted,
    });
  }, [onApi, permissions]);

  return <MeetPermissionsContext.Provider value={value}>{children}</MeetPermissionsContext.Provider>;
}

export function useMeetPermissionsContext() {
  const context = useContext(MeetPermissionsContext);

  if (!context) {
    throw new Error('useMeetPermissionsContext must be used within MeetPermissionsProvider');
  }

  return context;
}
